import React from 'react'

import { toJpeg, toPng } from 'html-to-image'
import { inject, observer } from 'mobx-react'
import type { Layout } from 'react-grid-layout'
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import DesktopEventStore from '~/client/src/desktop/stores/EventStore/DesktopEvents.store'
import { Loader } from '~/client/src/shared/components/Loader'
import {
  Empty,
  EmptyAction,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
} from '~/client/src/shared/components/shadcn-ui/empty'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import GraphExecutorStore from '~/client/src/shared/stores/domain/GraphExecutor.store'
import UserProjectsStore from '~/client/src/shared/stores/domain/UserProjects.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'

import { getWidgetChartType } from './components/ChartRenderer/ChartRenderer'
import DashboardHeader from './components/DashboardHeader/DashboardHeader'
import DateRangeSelectorStore from './components/DateRangeSelector/DateRangeSelector.store'
import SettingsPanel from './components/SettingsPanel/SettingsPanel'
import { ChartWidget, MetricWidget, TableWidget } from './components/widgets'
import { CHART_COLORS } from './Constants/Colors'
import { WidgetId } from './Constants/WidgetConfig'
import HubV2Store from './HubV2.store'
import { DashboardPreferencesData, LayoutManager } from './utils/LayoutManager'

import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'
import './HubV2.scss'

const ResponsiveGridLayout = React.lazy(async () => {
  const { Responsive, WidthProvider } = await import('react-grid-layout')
  return { default: WidthProvider(Responsive) }
})

interface IHubV2Props {
  graphExecutorStore?: GraphExecutorStore
  eventsStore?: DesktopEventStore
  projectDateStore?: ProjectDateStore
  userProjectsStore?: UserProjectsStore
}

@inject(
  'graphExecutorStore',
  'eventsStore',
  'projectDateStore',
  'userProjectsStore',
)
@observer
export default class HubV2 extends React.Component<IHubV2Props> {
  private store: HubV2Store
  private dateRangeSelectorStore: DateRangeSelectorStore

  private savePreferencesTimeout: NodeJS.Timeout | null = null

  public constructor(props: IHubV2Props) {
    super(props)
    const { graphExecutorStore, eventsStore, projectDateStore } = props
    if (!graphExecutorStore || !eventsStore || !projectDateStore) {
      throw new Error('HubV2 requires injected stores')
    }

    this.store = new HubV2Store(
      graphExecutorStore,
      eventsStore,
      projectDateStore,
    )
    this.dateRangeSelectorStore = new DateRangeSelectorStore(projectDateStore)

    LayoutManager.setPreferencesChangeCallback(this.handlePreferencesChange)
  }

  public componentDidMount() {
    this.store.fetchWorkflows()
    this.loadDashboardPreferences()
  }

  public render() {
    return (
      <div className="hub-v2">
        <DashboardHeader
          dateRangeSelectorStore={this.dateRangeSelectorStore}
          onDateRangeChange={this.handleDateRangeChange}
          onToggleSettings={this.handleToggleSettings}
          onResetLayout={this.handleResetLayout}
        />

        {this.store.isSettingsPanelOpen && (
          <SettingsPanel
            widgetVisibility={this.store.widgetVisibility}
            onClose={this.handleToggleSettings}
            onToggleWidgetVisibility={this.handleToggleWidgetVisibility}
          />
        )}

        <div className="hub-v2-content">
          {this.store.isLoading && (
            <Loader hint={Localization.translator.loading} />
          )}
          {this.store.error && this.renderError()}
          {!this.store.isLoading && !this.store.error && this.renderWidgets()}
        </div>
      </div>
    )
  }

  private renderError() {
    return (
      <Empty className="tw:min-h-100">
        <EmptyHeader>
          <EmptyDescription>{this.store.error}</EmptyDescription>
        </EmptyHeader>
        <EmptyContent>
          <EmptyAction onClick={() => this.store.fetchWorkflows()}>
            {Localization.translator.dashboard.retry}
          </EmptyAction>
        </EmptyContent>
      </Empty>
    )
  }

  private renderWidgets() {
    const visibleLayout = this.store.layout.filter(
      item => this.store.widgetVisibility[item.i as WidgetId],
    )

    // Create responsive layouts for different breakpoints
    const layouts = {
      lg: visibleLayout, // Desktop: 12 columns
      md: visibleLayout.map(item => ({
        ...item,
        w: Math.min(item.w * 2, 12), // Tablet: scale up widgets
        x: item.x >= 6 ? 0 : item.x, // Reflow to single column if needed
      })),
      sm: visibleLayout.map(item => ({
        ...item,
        w: 12, // Mobile: full width
        x: 0,
        static: false,
      })),
      xs: visibleLayout.map(item => ({
        ...item,
        w: 12, // Mobile: full width
        x: 0,
        static: false,
      })),
    }

    return (
      <React.Suspense
        fallback={<Loader hint={Localization.translator.loading} />}
      >
        <ResponsiveGridLayout
          className="widgets-grid"
          layouts={layouts}
          breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480 }}
          cols={{ lg: 12, md: 12, sm: 12, xs: 12 }}
          rowHeight={80}
          onLayoutChange={layout => this.handleLayoutChange(layout)}
          draggableHandle=".widget-header"
          isDraggable={true}
          isResizable={true}
          compactType="vertical"
          preventCollision={false}
        >
          {visibleLayout.map(item => (
            <div key={item.i}>{this.renderWidget(item.i as WidgetId)}</div>
          ))}
        </ResponsiveGridLayout>
      </React.Suspense>
    )
  }

  private renderWidget(widgetId: WidgetId) {
    switch (widgetId) {
      // Metric widgets
      case WidgetId.WORKFLOWS_COUNT:
        return (
          <MetricWidget
            widgetId={WidgetId.WORKFLOWS_COUNT}
            title={
              Localization.translator.dashboard.widgetTitles.totalWorkflows
            }
            value={this.store.workflowsCount}
            label={
              Localization.translator.dashboard.widgetLabels
                .activeWorkflowsInPeriod
            }
            comparison={this.store.workflowsCountComparison}
            isFlipped={this.store.flippedWidgets[WidgetId.WORKFLOWS_COUNT]}
            onFlip={this.handleFlipWidget}
          />
        )

      case WidgetId.COMPANIES_COUNT:
        return (
          <MetricWidget
            widgetId={WidgetId.COMPANIES_COUNT}
            title={Localization.translator.dashboard.widgetTitles.companies}
            value={this.store.companiesCount}
            label={
              Localization.translator.dashboard.widgetLabels
                .uniqueCompaniesInvolved
            }
            comparison={this.store.companiesCountComparison}
            isFlipped={this.store.flippedWidgets[WidgetId.COMPANIES_COUNT]}
            onFlip={this.handleFlipWidget}
          />
        )

      case WidgetId.USERS_COUNT:
        return (
          <MetricWidget
            widgetId={WidgetId.USERS_COUNT}
            title={Localization.translator.dashboard.widgetTitles.usersEditors}
            value={this.store.usersCount}
            label={
              Localization.translator.dashboard.widgetLabels.uniqueUsersEditing
            }
            comparison={this.store.usersCountComparison}
            isFlipped={this.store.flippedWidgets[WidgetId.USERS_COUNT]}
            onFlip={this.handleFlipWidget}
          />
        )

      case WidgetId.AVERAGE_DURATION:
        return (
          <MetricWidget
            widgetId={WidgetId.AVERAGE_DURATION}
            title={
              Localization.translator.dashboard.widgetTitles.averageDuration
            }
            value={this.store.averageDuration}
            label={
              Localization.translator.dashboard.widgetLabels
                .daysFromStartToCompletion
            }
            comparison={this.store.averageDurationComparison}
            isFlipped={this.store.flippedWidgets[WidgetId.AVERAGE_DURATION]}
            onFlip={this.handleFlipWidget}
          />
        )

      case WidgetId.ADVANCED_PLANNING:
        return (
          <MetricWidget
            widgetId={WidgetId.ADVANCED_PLANNING}
            title={
              Localization.translator.dashboard.widgetTitles.advancedPlanning
            }
            value={this.store.advancedPlanningDays}
            label={
              Localization.translator.dashboard.widgetLabels
                .timeRequestedBeforeStart
            }
            comparison={this.store.advancedPlanningComparison}
            isFlipped={this.store.flippedWidgets[WidgetId.ADVANCED_PLANNING]}
            onFlip={this.handleFlipWidget}
          />
        )

      case WidgetId.EQUIPMENT_BOOKINGS_WORKFLOWS:
        return (
          <MetricWidget
            widgetId={WidgetId.EQUIPMENT_BOOKINGS_WORKFLOWS}
            title={
              Localization.translator.dashboard.widgetTitles.equipmentBookings
            }
            value={this.store.equipmentBookingsCount}
            label={
              Localization.translator.dashboard.widgetLabels
                .workflowsWithEquipment
            }
            isFlipped={
              this.store.flippedWidgets[WidgetId.EQUIPMENT_BOOKINGS_WORKFLOWS]
            }
            onFlip={this.handleFlipWidget}
          />
        )

      case WidgetId.EQUIPMENT_BOOKINGS_HOURS:
        return (
          <MetricWidget
            widgetId={WidgetId.EQUIPMENT_BOOKINGS_HOURS}
            title={
              Localization.translator.dashboard.widgetTitles.equipmentHours
            }
            value={this.store.equipmentBookingsHours}
            label={
              Localization.translator.dashboard.widgetLabels
                .totalEquipmentBookingHours
            }
            isFlipped={
              this.store.flippedWidgets[WidgetId.EQUIPMENT_BOOKINGS_HOURS]
            }
            onFlip={this.handleFlipWidget}
          />
        )

      // Chart widgets
      case WidgetId.WORKFLOWS_BY_STATUS:
        return (
          <ChartWidget
            widgetId={WidgetId.WORKFLOWS_BY_STATUS}
            title={
              Localization.translator.dashboard.widgetTitles.workflowsByStatus
            }
            data={Object.entries(this.store.workflowsByStatus).map(
              ([name, value]) => ({ name, value }),
            )}
            chartName={Localization.translator.workflows}
            chartType={getWidgetChartType(
              WidgetId.WORKFLOWS_BY_STATUS,
              this.store.widgetChartTypes,
            )}
            isFlipped={this.store.flippedWidgets[WidgetId.WORKFLOWS_BY_STATUS]}
            isDownloading={
              this.store.downloadingWidgets[WidgetId.WORKFLOWS_BY_STATUS]
            }
            onFlip={this.handleFlipWidget}
            onChangeChartType={this.handleChangeChartType}
            onDownloadChart={this.handleDownloadChart}
          />
        )

      case WidgetId.ACTIVE_VS_COMPLETED:
        return (
          <ChartWidget
            widgetId={WidgetId.ACTIVE_VS_COMPLETED}
            title={
              Localization.translator.dashboard.widgetTitles.activeVsCompleted
            }
            data={[
              {
                name: Localization.translator.active,
                value: this.store.activeVsCompletedCount.active,
              },
              {
                name: Localization.translator.completed,
                value: this.store.activeVsCompletedCount.completed,
              },
            ]}
            chartName={Localization.translator.workflows}
            chartType={getWidgetChartType(
              WidgetId.ACTIVE_VS_COMPLETED,
              this.store.widgetChartTypes,
            )}
            isFlipped={this.store.flippedWidgets[WidgetId.ACTIVE_VS_COMPLETED]}
            isDownloading={
              this.store.downloadingWidgets[WidgetId.ACTIVE_VS_COMPLETED]
            }
            onFlip={this.handleFlipWidget}
            onChangeChartType={this.handleChangeChartType}
            onDownloadChart={this.handleDownloadChart}
          />
        )

      case WidgetId.WORKFLOWS_BY_CATEGORY:
        return (
          <ChartWidget
            widgetId={WidgetId.WORKFLOWS_BY_CATEGORY}
            title={
              Localization.translator.dashboard.widgetTitles.workflowsByCategory
            }
            data={Object.entries(this.store.workflowsByCategory).map(
              ([name, value]) => ({ name, value }),
            )}
            chartName={Localization.translator.workflows}
            chartType={getWidgetChartType(
              WidgetId.WORKFLOWS_BY_CATEGORY,
              this.store.widgetChartTypes,
            )}
            isFlipped={
              this.store.flippedWidgets[WidgetId.WORKFLOWS_BY_CATEGORY]
            }
            isDownloading={
              this.store.downloadingWidgets[WidgetId.WORKFLOWS_BY_CATEGORY]
            }
            onFlip={this.handleFlipWidget}
            onChangeChartType={this.handleChangeChartType}
            onDownloadChart={this.handleDownloadChart}
          />
        )

      case WidgetId.WORKFLOWS_TIMELINE:
        return this.renderWorkflowsTimelineWidget()

      case WidgetId.LOCATIONS_WORKFLOWS:
        return (
          <ChartWidget
            widgetId={WidgetId.LOCATIONS_WORKFLOWS}
            title={
              Localization.translator.dashboard.widgetTitles.locationsWorkflows
            }
            data={Object.entries(this.store.workflowsByLocation).map(
              ([name, value]) => ({ name, value }),
            )}
            chartName={Localization.translator.workflows}
            chartType={getWidgetChartType(
              WidgetId.LOCATIONS_WORKFLOWS,
              this.store.widgetChartTypes,
            )}
            isFlipped={this.store.flippedWidgets[WidgetId.LOCATIONS_WORKFLOWS]}
            isDownloading={
              this.store.downloadingWidgets[WidgetId.LOCATIONS_WORKFLOWS]
            }
            onFlip={this.handleFlipWidget}
            onChangeChartType={this.handleChangeChartType}
            onDownloadChart={this.handleDownloadChart}
          />
        )

      case WidgetId.LOCATIONS_BY_TYPE:
        return (
          <ChartWidget
            widgetId={WidgetId.LOCATIONS_BY_TYPE}
            title={
              Localization.translator.dashboard.widgetTitles.locationsByType
            }
            data={Object.entries(this.store.locationsByType).map(
              ([name, value]) => ({ name, value }),
            )}
            chartName={Localization.translator.locations}
            chartType={getWidgetChartType(
              WidgetId.LOCATIONS_BY_TYPE,
              this.store.widgetChartTypes,
            )}
            isFlipped={this.store.flippedWidgets[WidgetId.LOCATIONS_BY_TYPE]}
            isDownloading={
              this.store.downloadingWidgets[WidgetId.LOCATIONS_BY_TYPE]
            }
            onFlip={this.handleFlipWidget}
            onChangeChartType={this.handleChangeChartType}
            onDownloadChart={this.handleDownloadChart}
          />
        )

      // Table widget
      case WidgetId.RECENT_WORKFLOWS:
        return (
          <TableWidget
            widgetId={WidgetId.RECENT_WORKFLOWS}
            title={
              Localization.translator.dashboard.widgetTitles.recentWorkflows
            }
            workflows={this.store.recentWorkflows}
            isFlipped={this.store.flippedWidgets[WidgetId.RECENT_WORKFLOWS]}
            onFlip={this.handleFlipWidget}
          />
        )

      default:
        return null
    }
  }

  // Special rendering for timeline widget (uses LineChart)
  private renderWorkflowsTimelineWidget() {
    const chartType = getWidgetChartType(
      WidgetId.WORKFLOWS_TIMELINE,
      this.store.widgetChartTypes,
    )
    const timelineData = this.store.workflowsTimeline.map(item => ({
      name: item.date,
      value: item.count,
    }))

    const lineChartContent =
      chartType === 'line_chart' && timelineData.length > 0 ? (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={this.store.workflowsTimeline}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line
              type="monotone"
              dataKey="count"
              stroke={CHART_COLORS[0]}
              strokeWidth={2}
              name={Localization.translator.dashboard.workflowsCreated}
            />
          </LineChart>
        </ResponsiveContainer>
      ) : undefined

    return (
      <ChartWidget
        widgetId={WidgetId.WORKFLOWS_TIMELINE}
        title={Localization.translator.dashboard.widgetTitles.workflowsTimeline}
        data={timelineData}
        chartName={Localization.translator.workflows}
        chartType={chartType}
        isFlipped={this.store.flippedWidgets[WidgetId.WORKFLOWS_TIMELINE]}
        isDownloading={
          this.store.downloadingWidgets[WidgetId.WORKFLOWS_TIMELINE]
        }
        onFlip={this.handleFlipWidget}
        onChangeChartType={this.handleChangeChartType}
        onDownloadChart={this.handleDownloadChart}
      >
        {lineChartContent}
      </ChartWidget>
    )
  }

  // Event handlers
  private handleDateRangeChange = () => {
    this.store.setDateRange(this.dateRangeSelectorStore.dateRange)
  }

  private handleLayoutChange = (layout: Layout[]) => {
    const updatedLayout = this.store.layout.map(existingItem => {
      const updated = layout.find(l => l.i === existingItem.i)
      return updated || existingItem
    })

    this.store.setLayout(updatedLayout)
    LayoutManager.saveLayout(updatedLayout)
  }

  private handleResetLayout = () => {
    const defaultLayout = LayoutManager.resetLayout()
    this.store.setLayout(defaultLayout)
  }

  private handleToggleSettings = () => {
    this.store.toggleSettingsPanel()
  }

  private handleToggleWidgetVisibility = (widgetId: string) => {
    const newVisibility = !this.store.widgetVisibility[widgetId]
    LayoutManager.saveWidgetVisibility(widgetId, newVisibility)
    this.store.setWidgetVisibility(widgetId, newVisibility)
  }

  private handleFlipWidget = (widgetId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    this.store.toggleWidgetFlip(widgetId)
  }

  private handleChangeChartType = (widgetId: string, chartType: string) => {
    this.store.setWidgetChartType(widgetId, chartType)
    LayoutManager.saveWidgetChartType(widgetId, chartType)
  }

  private handleDownloadChart = async (
    widgetId: string,
    widgetTitle: string,
    format: 'png' | 'jpeg',
  ) => {
    const widgetElement = document.querySelector(
      `[data-widget-id="${widgetId}"]`,
    ) as HTMLElement
    if (!widgetElement) {
      console.error('Widget element not found')
      return
    }

    this.store.setDownloadingWidget(widgetId, true)

    // html-to-image drops a rejected node's children too.
    const captureOptions = {
      pixelRatio: 2,
      backgroundColor: '#ffffff',
      cacheBust: true,
      filter: (node: HTMLElement) => node.dataset?.exportExclude === undefined,
    }

    try {
      let dataUrl: string

      if (format === 'png') {
        dataUrl = await toPng(widgetElement, captureOptions)
      } else {
        dataUrl = await toJpeg(widgetElement, {
          ...captureOptions,
          quality: 0.95,
        })
      }

      const link = document.createElement('a')
      link.download = `${widgetTitle.toLowerCase().replace(/\s+/g, '-')}-${
        new Date().toISOString().split('T')[0]
      }.${format}`
      link.href = dataUrl
      link.click()
    } catch (error) {
      console.error('Error downloading chart:', error)
    } finally {
      this.store.setDownloadingWidget(widgetId, false)
    }
  }

  // Preference management
  private handlePreferencesChange = (prefs: DashboardPreferencesData) => {
    if (this.savePreferencesTimeout) {
      clearTimeout(this.savePreferencesTimeout)
    }

    this.savePreferencesTimeout = setTimeout(() => {
      this.saveDashboardPreferences(prefs)
    }, 1000)
  }

  private loadDashboardPreferences = () => {
    const userActiveProjectSettings =
      this.props.eventsStore?.appState?.userActiveProjectSettings

    if (userActiveProjectSettings?.dashboardPreferences) {
      LayoutManager.initializeFromBackend(
        userActiveProjectSettings.dashboardPreferences,
      )
    }

    const layout = LayoutManager.getLayout()
    const widgetVisibility = LayoutManager.loadWidgetVisibility()
    const chartTypes = LayoutManager.loadWidgetChartTypes()

    const defaultVisibility: Record<string, boolean> = {}
    Object.values(WidgetId).forEach(id => {
      defaultVisibility[id] =
        widgetVisibility[id] !== undefined ? widgetVisibility[id] : true
    })

    this.store.applyPreferences({
      layout,
      widgetVisibility: defaultVisibility,
      widgetChartTypes: chartTypes,
    })
  }

  private saveDashboardPreferences = (prefs: DashboardPreferencesData) => {
    const userActiveProjectSettings =
      this.props.eventsStore?.appState?.userActiveProjectSettings

    if (!userActiveProjectSettings) {
      return
    }

    userActiveProjectSettings.dashboardPreferences = {
      layout: prefs.layout ? JSON.stringify(prefs.layout) : null,
      widgetVisibility: prefs.widgetVisibility
        ? JSON.stringify(prefs.widgetVisibility)
        : null,
      chartTypes: prefs.chartTypes ? JSON.stringify(prefs.chartTypes) : null,
    }

    if (!this.props.userProjectsStore) {
      return
    }

    this.props.userProjectsStore.save([userActiveProjectSettings.toDto()])
  }
}
